import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

export const usePayment = (id) => {
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (id) {
      fetchPayment();
    }
  }, [id]);

  const fetchPayment = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('payments')
        .select(`
          *,
          students (full_name, email,nric_passport, contact_no),
          payment_enrollments (
            enrollment_id,
            amount_applied,
            enrollments (
              id,
              subjects (name),
              intakes (name)
            )
          )
        `)
        .eq('id', id)
        .eq('is_deleted', false)
        .single();
      
      if (error) throw error;
      
      // Transform data to include enrollment details
      const transformedData = {
        ...data,
        student_name: data.students?.full_name || '',
        student_email: data.students?.email || '',
        student_nric_passport: data.students?.nric_passport || '',
        student_contact_no: data.students?.contact_no || '',
        enrollments: data.payment_enrollments?.map(pe => ({
          enrollment_id: pe.enrollment_id,
          amount_applied: pe.amount_applied,
          subject_name: pe.enrollments?.subjects?.name || '',
          intake_name: pe.enrollments?.intakes?.name || ''
        })) || []
      };
      
      setPayment(transformedData);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const updatePayment = async (updates) => {
    try {
      console.log('Updates received:', updates);

      // 👇 keep your existing validColumns array
      const validColumns = [
        'student_id',
        'invoice_number',
        'amount',
        'payment_method',
        'payment_date',
        'status',
        'payment_type',
        'notes',
        'receipt_url'
      ];

      // 👇 keep your existing filter logic
      const validUpdates = {};
      for (const key in updates) {
        if (validColumns.includes(key)) {
          validUpdates[key] = updates[key];
        }
      }

      console.log('Valid updates for payments table:', validUpdates);

      // ⬇️ IMPORTANT: select().single() so we can return data and DO NOT throw
      const { data, error } = await supabase
        .from('payments')
        .update(validUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        // Return a structured result (do not throw)
        return {
          ok: false,
          error: error.message || 'Failed to update payment',
          code: error.code,         // e.g. '23505' for unique violation
          details: error.details
        };
      }

      // Optional: sync local state optimistically
      setPayment((prev) => (prev ? { ...prev, ...validUpdates } : prev));

      return { ok: true, data };
    } catch (e) {
      // Catch network/unexpected errors but still DO NOT throw
      return {
        ok: false,
        error: e?.message || 'Unexpected error while updating payment'
      };
    }
  };


  const deletePayment = async () => {
    try {
      // First, hard delete the payment_enrollments records related to this payment
      //const { error: peError } = await supabase
      //  .from('payment_enrollments')
      //  .delete()
      //  .eq('payment_id', id);
      
      //if (peError) {
      //  console.error('Error deleting payment enrollments:', peError);
      //  throw peError;
      //}
      
      // Then, soft delete the payment record
      const { error } = await supabase
        .from('payments')
        .update({ is_deleted: true })
        .eq('id', id);
      
      if (error) throw error;
      
      return true;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  return {
    payment,
    loading,
    error,
    updatePayment,
    deletePayment,
    refetch: fetchPayment
  };
};